import { useEffect, useState } from "react";
import { supAPI, fmtINR } from "./supApi";
import { toast } from "sonner";
import { Plus, X, Save } from "lucide-react";
import { NumberInput } from "@/components/ui/number-input";

export default function SupplyItems() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => {
    supAPI.items({ q: q || undefined, category_id: catFilter || undefined }).then(setItems).catch(() => {});
  };
  useEffect(() => { load(); }, [q, catFilter]); // eslint-disable-line
  useEffect(() => {
    supAPI.categories({ only_enabled: true }).then(setCats);
    supAPI.vendors().then(setVendors);
  }, []);

  return (
    <div className="space-y-4" data-testid="sup-items">
      <div className="flex flex-wrap items-center gap-3">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or SKU…" aria-label="Search items by name or SKU"
               data-testid="sup-items-search"
               className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] min-w-[240px]"/>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
                data-testid="sup-items-cat-filter" aria-label="Filter by category"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All categories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button onClick={() => setCreating(true)} data-testid="sup-items-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> Add Item
        </button>
      </div>

      {!cats.length ? (
        <EmptyState msg="No categories enabled yet. Go to Settings to enable the categories your hotel uses (F&B, HK, Engineering, etc.)."/>
      ) : !items.length ? (
        <EmptyState msg="No items yet. Create your first inventory item."/>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-left px-4 py-3">UoM</th>
                <th className="text-right px-4 py-3">Reorder ≤</th>
                <th className="text-right px-4 py-3">Std. Cost</th>
                <th className="text-left px-4 py-3">Vendor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(i => {
                const v = vendors.find(x => x.id === i.preferred_vendor_id);
                return (
                  <tr key={i.id} data-testid={`sup-item-row-${i.id}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 mono text-slate-500">{i.sku}</td>
                    <td className="px-4 py-3 font-medium text-hocp-navy">{i.name}</td>
                    <td className="px-4 py-3 text-slate-500">{i.uom}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{i.reorder_level}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtINR(i.standard_cost)}</td>
                    <td className="px-4 py-3 text-slate-600">{v?.name || <span className="text-slate-300">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ItemDialog cats={cats} vendors={vendors}
                    onClose={() => setCreating(false)}
                    onCreated={() => { setCreating(false); load(); }}/>
      )}
    </div>
  );
}

function ItemDialog({ cats, vendors, onClose, onCreated }) {
  const [f, setF] = useState({ sku: "", name: "", category_id: cats[0]?.id || "", uom: "pcs",
                                 reorder_level: 0, reorder_qty: 0, standard_cost: 0,
                                 preferred_vendor_id: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.sku || !f.name || !f.category_id) { toast.error("SKU, name and category are required"); return; }
    setSaving(true);
    try {
      await supAPI.createItem({
        ...f, reorder_level: +f.reorder_level, reorder_qty: +f.reorder_qty,
        standard_cost: +f.standard_cost,
        preferred_vendor_id: f.preferred_vendor_id || undefined,
      });
      toast.success("Item created"); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Item</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Fld label="SKU"><input value={f.sku} onChange={e => setF({...f, sku: e.target.value.toUpperCase()})} data-testid="new-item-sku" className={inCls}/></Fld>
          <Fld label="Name"><input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="new-item-name" className={inCls}/></Fld>
          <Fld label="Category">
            <select value={f.category_id} onChange={e => setF({...f, category_id: e.target.value})} data-testid="new-item-cat" className={inCls}>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Fld>
          <Fld label="Unit of Measure"><input value={f.uom} onChange={e => setF({...f, uom: e.target.value})} data-testid="new-item-uom" className={inCls}/></Fld>
          <Fld label="Reorder Level"><NumberInput value={f.reorder_level} onChange={e => setF({...f, reorder_level: e.target.value})} className={inCls}/></Fld>
          <Fld label="Reorder Qty"><NumberInput value={f.reorder_qty} onChange={e => setF({...f, reorder_qty: e.target.value})} className={inCls}/></Fld>
          <Fld label="Std. Cost (₹)"><NumberInput value={f.standard_cost} onChange={e => setF({...f, standard_cost: e.target.value})} className={inCls}/></Fld>
          <Fld label="Preferred Vendor">
            <select value={f.preferred_vendor_id} onChange={e => setF({...f, preferred_vendor_id: e.target.value})} className={inCls}>
              <option value="">— none —</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Fld>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="new-item-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium disabled:opacity-60 inline-flex items-center gap-2">
            <Save className="w-4 h-4"/> {saving ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inCls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";
function Fld({ label, children }) {
  return <label className="block"><div className="text-[11.5px] text-slate-500 mb-1 font-medium">{label}</div>{children}</label>;
}

export function EmptyState({ msg }) {
  return <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-[13px] text-slate-500">{msg}</div>;
}
